import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import {
  calcFilterPos,
  calcOpDropdownPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "../waterfall-eac/filterPopoverUtils";
import {
  NumericFilterContent,
  TextFilterContent,
  fpBase,
} from "../waterfall-eac/tableColumnFilterShared";
import {
  ctApplyNumFilter,
  ctFBtnSt,
  FilterIcon,
  CT_OP_ENTRIES,
} from "../scenario-yoy/yoyTableShared";
import { TH_ACTUAL_TEXT } from "../../reportTableConfig";

/** @typedef {{ key: string, label: string, isText?: boolean, getValue?: (row: any) => any }} LuCol */

export function useLuTableSortFilter(cols, rows) {
  const [sortKeys, setSortKeys] = useState([]);
  const [textFilters, setTextFilters] = useState({});
  const [numFilters, setNumFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  const colByKey = useMemo(
    () => Object.fromEntries(cols.map((c) => [c.key, c])),
    [cols],
  );

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  const getCellValue = useCallback(
    (row, key) => {
      const col = colByKey[key];
      if (col?.getValue) return col.getValue(row);
      return row[key];
    },
    [colByKey],
  );

  const getSortColumnLabel = useCallback(
    (key) => colByKey[key]?.label || key,
    [colByKey],
  );

  const getSortValue = useCallback(
    (row, key) => {
      const col = colByKey[key];
      const v = getCellValue(row, key);
      if (col && !col.isText) return Number(v) || 0;
      // Text columns (e.g. Project ID) must stay strings for localeCompare
      return v == null ? "" : String(v);
    },
    [colByKey, getCellValue],
  );

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const isTextActive = useCallback(
    (key) => !!(textFilters[key] && String(textFilters[key]).trim()),
    [textFilters],
  );

  const isNumActive = useCallback(
    (key) => {
      const f = numFilters[key];
      return !!(f && f.value !== "" && f.value !== undefined);
    },
    [numFilters],
  );

  const hasFilterValue = useCallback(
    (key) => {
      const col = colByKey[key];
      return col?.isText ? isTextActive(key) : isNumActive(key);
    },
    [colByKey, isTextActive, isNumActive],
  );

  const toggleFilter = useCallback(
    (key, btnEl) => {
      if (openFilterKey === key) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
        return;
      }
      const btn = btnEl || filterBtnRefs.current[key];
      if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      setOpenFilterKey(key);
      setOpenOpKey(null);
    },
    [openFilterKey],
  );

  const toggleOpDropdown = useCallback(
    (key) => {
      if (openOpKey === key) {
        setOpenOpKey(null);
        return;
      }
      const btn = opBtnRefs.current[key];
      if (btn) setOpPos(calcOpDropdownPos(btn));
      setOpenOpKey(key);
    },
    [openOpKey],
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      for (const col of cols) {
        if (col.isText) {
          const q = textFilters[col.key]?.trim().toLowerCase();
          if (
            q &&
            !String(getCellValue(row, col.key) ?? "")
              .toLowerCase()
              .includes(q)
          )
            return false;
        } else if (
          isNumActive(col.key) &&
          !ctApplyNumFilter(getCellValue(row, col.key), numFilters[col.key])
        ) {
          return false;
        }
      }
      return true;
    });
  }, [rows, cols, textFilters, numFilters, getCellValue, isNumActive]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const hasTableFilters =
    cols.some((c) =>
      c.isText
        ? textFilters[c.key]?.trim()
        : numFilters[c.key]?.value !== undefined &&
          numFilters[c.key]?.value !== "",
    );

  const renderFilterPanel = () => {
    if (!openFilterKey) return null;
    const col = colByKey[openFilterKey];
    if (!col) return null;
    return createPortal(
      <>
        <div
          data-filter-panel
          style={{
            ...fpBase,
            top: filterPos.top,
            left: filterPos.left,
            width: FILTER_PANEL_WIDTH_NUMERIC,
          }}
        >
          {col.isText ? (
            <TextFilterContent
              label={col.label}
              value={textFilters[openFilterKey] || ""}
              onChange={(v) =>
                setTextFilters((p) => ({ ...p, [openFilterKey]: v }))
              }
              onClear={() =>
                setTextFilters((p) => {
                  const n = { ...p };
                  delete n[openFilterKey];
                  return n;
                })
              }
            />
          ) : (
            <NumericFilterContent
              label={col.label}
              value={numFilters[openFilterKey]?.value ?? ""}
              op={numFilters[openFilterKey]?.op || "gt"}
              onValueChange={(v) =>
                setNumFilters((p) => ({
                  ...p,
                  [openFilterKey]: {
                    op: "gt",
                    ...p[openFilterKey],
                    value: v,
                  },
                }))
              }
              onClear={() =>
                setNumFilters((p) => {
                  const n = { ...p };
                  delete n[openFilterKey];
                  return n;
                })
              }
              onToggleOp={() => toggleOpDropdown(openFilterKey)}
              opBtnRef={(el) => {
                opBtnRefs.current[openFilterKey] = el;
              }}
            />
          )}
        </div>
        {openOpKey &&
          createPortal(
            <div data-op-dd style={{ ...opDropStyle, top: opPos.top, left: opPos.left }}>
              {CT_OP_ENTRIES.map((e) => (
                <button
                  key={e.op}
                  type="button"
                  data-op-btn
                  style={opItemStyle(
                    (numFilters[openOpKey]?.op || "gt") === e.op,
                  )}
                  onClick={() => {
                    setNumFilters((p) => ({
                      ...p,
                      [openOpKey]: { ...p[openOpKey], op: e.op, value: p[openOpKey]?.value ?? "" },
                    }));
                    setOpenOpKey(null);
                  }}
                >
                  <span style={{ fontWeight: 700, width: 18 }}>{e.sym}</span>
                  {e.label}
                </button>
              ))}
            </div>,
            document.body,
          )}
      </>,
      document.body,
    );
  };

  return {
    sortKeys,
    sortedRows,
    filteredRows,
    hasTableFilters,
    openFilterKey,
    onThClick,
    getSortColumnLabel,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
    renderFilterPanel,
    textFilters,
    numFilters,
  };
}

const opDropStyle = {
  position: "fixed",
  zIndex: 10000,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.13)",
  padding: 4,
  minWidth: 160,
};

const opItemStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "6px 10px",
  border: "none",
  background: active ? "#EEF2FF" : "transparent",
  color: active ? "#4F46E5" : "#374151",
  fontSize: 12,
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
  borderRadius: 5,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  textAlign: "left",
});

export function LuSortFilterTh({
  col,
  className,
  style,
  sortKeys,
  onThClick,
  getSortColumnLabel,
  openFilterKey,
  hasFilterValue,
  toggleFilter,
  filterBtnRefs,
  align = "left",
  isActual = false,
}) {
  const active = openFilterKey === col.key || hasFilterValue(col.key);
  return (
    <th
      className={className}
      style={{ ...style, cursor: "pointer" }}
      onClick={onThClick(col.key)}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: align === "right" ? "flex-end" : "space-between",
          gap: 4,
          minWidth: 0,
        }}
      >
        <WaterfallThLabel
          label={col.label}
          colKey={col.key}
          sortKeys={sortKeys}
          getLabel={getSortColumnLabel}
          highlight={openFilterKey === col.key}
          align={align}
          textColor={isActual && openFilterKey !== col.key ? TH_ACTUAL_TEXT : undefined}
          tone={isActual ? "actual" : "default"}
        />
        <button
          ref={(el) => {
            filterBtnRefs.current[col.key] = el;
          }}
          data-filter-btn
          type="button"
          style={{
            ...ctFBtnSt(active),
            ...(isActual
              ? {
                  color: TH_ACTUAL_TEXT,
                  opacity: active ? 1 : 0.55,
                  background: active ? "rgba(0,0,0,0.12)" : "transparent",
                }
              : {}),
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleFilter(col.key, e.currentTarget);
          }}
          title="Filter"
        >
          <FilterIcon />
        </button>
      </div>
    </th>
  );
}
